import React, {
    FC,
    PropsWithChildren,
    Suspense,
    useEffect,
    useState,
} from 'react'
import {
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom'
import Loader from '../components/common/loader'
import DashboardLayout from '../containers/DashboardLayout'
import AuthProvider from '../providers/contexts/Auth'
import { DashboardProvider } from '../providers/contexts/Dashboard'
import OnboardingFormProvider from '../providers/contexts/OnboardingForm'
import { TreatmentProvider } from '../providers/contexts/Treatment'
import { UserDataProvider } from '../providers/contexts/UserDataProvider'
import { MixPanel } from '../services/mixpanelActions'
import { SearchParamKeys } from '../utils/constants'
import { PARENT_ROUTES, ROUTES } from '../utils/constants/enum'
import CheckForDevice from '../utils/routes/checkForDevice'
import AccountsRouter from './AccountsRouter'
import PageNotFound from './pages/404'
import DownloadApp from './pages/download-app'
import routes from './routes'
import { Helmet } from 'react-helmet'
import { removeHubspotScripts } from '../utils/helper-functions'
import { HubspotProvider } from '../providers/contexts/HubspotContext'

const CookieConsentWrapper = React.lazy(
    () => import('./../containers/cookie-consent/index') as Promise<any>
)
const NoCookieConsentWrapper = React.lazy(
    () => import('./../containers/no-cookie-consent/index') as Promise<any>
)

export const ContentRouter = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const [params] = useSearchParams()
    const [accountsRouterLoaded, setAccountsRouterLoaded] = useState(false)

    useEffect(() => {
        if (!location.pathname.includes(`${ROUTES.ACCOUNTS}`)) {
            setAccountsRouterLoaded(true)
        }
        if (location.pathname.includes(ROUTES.ACCOUNTS))
            setAccountsRouterLoaded(false)
    }, [location])

    useEffect(() => {
        const pageName = routes
            .find((item) => location.pathname === item.path)
            ?.name.split(' ')
            .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
            .join(' ')
        MixPanel.actions.trackPageView(pageName ?? '')

        let inEffect = true
        if (inEffect) {
            window.scrollTo(0, 0)
        }

        if (
            location.pathname.startsWith(`${ROUTES.ACCOUNTS_SIGNUP}`) ||
            location.pathname.startsWith(`signup`) ||
            location.pathname.startsWith(ROUTES.ACCOUNTS_SIGNIN) ||
            location.pathname.startsWith('signin')
        ) {
            if (
                (params.has(SearchParamKeys.KEY) &&
                    params.get(SearchParamKeys.KEY)) ||
                (params.has(SearchParamKeys.ESTI_KEY) &&
                    params.get(SearchParamKeys.ESTI_KEY))
            ) {
                navigate(`${ROUTES.SIGNUP}?${params.toString()}`)
            } else navigate(`${ROUTES.ACCOUNTS}${location.pathname}`)
        }

        return () => {
            inEffect = false
        }
    }, [location])

    return (
        <Suspense fallback={<Loader />}>
            <HubspotProvider>
                <Routes>
                    {routes
                        .filter((route) => route.childOf === undefined)
                        .map((route) => {
                            return (
                                route.component && (
                                    <Route
                                        key={`route-${route.name}`}
                                        path={route.path}
                                        element={
                                            <CookieConsentWrapper>
                                                <route.component />
                                            </CookieConsentWrapper>
                                        }
                                    />
                                )
                            )
                        })}
                    {/* Added redirection rule for mybeautyhealth-loyalty-rewards/. @TODO: Need to handle it better */}
                    <Route
                        key={`route-mybeautyhealth-loyalty-rewards`}
                        path={'mybeautyhealth-loyalty-rewards/'}
                        element={<Navigate to={'/'} replace />}
                    />
                    <Route
                        path="/accounts/*"
                        element={
                            <NoCookieConsentWrapper>
                                <UserDataProvider>
                                    <AuthProvider>
                                        <OnboardingFormProvider>
                                            <AccountsRouter
                                                onLoad={() =>
                                                    setAccountsRouterLoaded(
                                                        true
                                                    )
                                                }
                                            />
                                        </OnboardingFormProvider>
                                    </AuthProvider>
                                </UserDataProvider>
                            </NoCookieConsentWrapper>
                        }
                    />
                    <Route
                        path="/dashboard/treatment"
                        element={
                            <UserDataProvider>
                                <NoCookieConsentWrapper>
                                    <DashboardProvider>
                                        <TreatmentProvider>
                                            <DashboardLayout />
                                        </TreatmentProvider>
                                    </DashboardProvider>
                                </NoCookieConsentWrapper>
                            </UserDataProvider>
                        }
                    >
                        {routes
                            .filter((route) =>
                                route.path.startsWith('/dashboard/treatment')
                            )
                            .map((route) => (
                                <Route
                                    key={`route-${route.name}`}
                                    path={route.path}
                                    element={<route.component />}
                                />
                            ))}
                    </Route>
                    <Route
                        path="/dashboard"
                        element={
                            <UserDataProvider>
                                <NoCookieConsentWrapper>
                                    <DashboardProvider>
                                        <DashboardLayout />
                                    </DashboardProvider>
                                </NoCookieConsentWrapper>
                            </UserDataProvider>
                        }
                    >
                        {routes
                            .filter(
                                (route) =>
                                    route.childOf === PARENT_ROUTES.DASHBOARD
                            )
                            .map((route) => (
                                <Route
                                    key={`route-${route.name}`}
                                    path={route.path}
                                    element={<route.component />}
                                />
                            ))}
                    </Route>
                    <Route
                        path="/download-app"
                        element={
                            <CheckForDevice>
                                <DownloadApp />
                            </CheckForDevice>
                        }
                    />
                    {accountsRouterLoaded ? (
                        <Route
                            key="404"
                            path="*"
                            element={<PageNotFound></PageNotFound>}
                        ></Route>
                    ) : null}
                </Routes>
            </HubspotProvider>
        </Suspense>
    )
}
